import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import dashboardService, {
  DashboardKpis,
  DashboardOverview,
  OverviewCandidate,
  OverviewCompany,
  OverviewUserAppointments,
} from '../../services/dashboard.service';
import { candidatesService, Candidate } from '../../services/candidates.service';
import KpiRow from '../recruiting-dashboard/KpiRow';
import '../recruiting-dashboard/recruiting-dashboard.css';
import CandidateDetailModalV2 from '../candidates/CandidateDetailModalV2';
import { stageInfo, stageLabel } from '../../constants/candidateStage';

// Statusfarben und -bezeichnungen kommen aus der zentralen Stufendefinition.

const InfoLine: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div style={{ display: 'flex', gap: '8px', fontSize: '0.85rem' }}>
    <span style={{ color: 'var(--text-tertiary)', minWidth: '120px' }}>{label}</span>
    <span style={{ color: 'var(--text-primary)', fontWeight: 600 }}>{value}</span>
  </div>
);

const StatusBadge: React.FC<{ stage?: string | null }> = ({ stage }) => {
  if (!stage) return null;
  const c = stageInfo(stage).color;
  return (
    <span
      style={{
        fontSize: '0.72rem',
        fontWeight: 700,
        color: c,
        background: `${c}1a`,
        border: `1px solid ${c}40`,
        borderRadius: '999px',
        padding: '2px 8px',
        whiteSpace: 'nowrap',
        flexShrink: 0,
      }}
    >
      {stageLabel(stage, true)}
    </span>
  );
};

// ---------------------------------------------------------------------------
// Hilfsfunktionen (Datums-/Zeitformatierung)
// ---------------------------------------------------------------------------
const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();

function relativeDay(iso?: string | null): string {
  if (!iso) return '';
  const diff = Math.round((startOfDay(new Date()) - startOfDay(new Date(iso))) / 86400000);
  if (diff <= 0) return 'heute';
  if (diff === 1) return 'gestern';
  return `vor ${diff} Tagen`;
}

function weekdayTime(iso?: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  const wd = d.toLocaleDateString('de-DE', { weekday: 'short' });
  const t = d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
  return `${wd} ${t}`;
}

function timeShort(hms?: string | null): string {
  if (!hms) return '';
  return hms.slice(0, 5); // 'HH:MM:SS' → 'HH:MM'
}

const todayLong = () =>
  new Date().toLocaleDateString('de-DE', { weekday: 'long', day: '2-digit', month: 'long' });

// ---------------------------------------------------------------------------
// Kachel-Rahmen
// ---------------------------------------------------------------------------
interface CardProps {
  icon: string;
  title: string;
  subtitle: string;
  count: number;
  emptyText: string;
  children: React.ReactNode;
  wide?: boolean;
}

const DashboardCard: React.FC<CardProps> = ({ icon, title, subtitle, count, emptyText, children, wide }) => (
  <div
    className="glass-container"
    style={{
      padding: 'var(--spacing-lg)',
      borderRadius: 'var(--radius-lg)',
      border: '1px solid var(--glass-border)',
      display: 'flex',
      flexDirection: 'column',
      minHeight: wide ? 'auto' : '260px',
      maxHeight: wide ? 'none' : '420px',
      gridColumn: wide ? '1 / -1' : 'auto',
    }}
  >
    <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px', marginBottom: '2px' }}>
      <span style={{ fontSize: '1.1rem' }}>{icon}</span>
      <h3 style={{ fontSize: '1.05rem', fontWeight: 700, color: 'var(--text-primary)', margin: 0 }}>{title}</h3>
      <span
        style={{
          marginLeft: 'auto',
          fontSize: '0.8rem',
          fontWeight: 700,
          color: 'var(--accent-primary)',
          background: 'rgba(59,130,246,0.12)',
          borderRadius: '999px',
          padding: '2px 10px',
        }}
      >
        {count}
      </span>
    </div>
    <div style={{ fontSize: '0.8rem', color: 'var(--text-tertiary)', marginBottom: 'var(--spacing-md)' }}>
      {subtitle}
    </div>
    <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '8px' }}>
      {count === 0 ? (
        <div style={{ color: 'var(--text-tertiary)', fontSize: '0.9rem', padding: '8px 0' }}>{emptyText}</div>
      ) : (
        children
      )}
    </div>
  </div>
);

const Row: React.FC<{ onClick?: () => void; children: React.ReactNode }> = ({ onClick, children }) => (
  <div
    onClick={onClick}
    style={{
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      padding: '10px 12px',
      borderRadius: 'var(--radius-md)',
      background: 'var(--glass)',
      border: '1px solid var(--glass-border)',
      cursor: onClick ? 'pointer' : 'default',
    }}
  >
    {children}
  </div>
);

const primaryText: React.CSSProperties = {
  fontSize: '0.9rem',
  fontWeight: 600,
  color: 'var(--text-primary)',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};
const secondaryText: React.CSSProperties = { fontSize: '0.78rem', color: 'var(--text-secondary)' };

// ---------------------------------------------------------------------------
// Fokus-Dashboard
// ---------------------------------------------------------------------------
export const FocusDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [data, setData] = useState<DashboardOverview | null>(null);
  const [kpis, setKpis] = useState<DashboardKpis | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Kandidaten-Popup + volle Detailansicht
  const [popupCandidate, setPopupCandidate] = useState<OverviewCandidate | null>(null);
  const [modalCandidate, setModalCandidate] = useState<Candidate | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [loadingFull, setLoadingFull] = useState(false);

  const ladeUebersicht = React.useCallback(async (mitLadeanzeige = true) => {
    try {
      if (mitLadeanzeige) setLoading(true);
      const [overview, kpiDaten] = await Promise.all([
        dashboardService.getOverview(),
        dashboardService.getKpis().catch(() => null),
      ]);
      setData(overview);
      setKpis(kpiDaten);
      setError(null);
    } catch (err) {
      console.error('FocusDashboard load error:', err);
      setError('Übersicht konnte nicht geladen werden.');
    } finally {
      if (mitLadeanzeige) setLoading(false);
    }
  }, []);

  // Laufend aktuell halten: alle 60 s und immer dann, wenn der Tab
  // wieder in den Vordergrund kommt.
  useEffect(() => {
    ladeUebersicht(true);

    const intervall = setInterval(() => ladeUebersicht(false), 60000);
    const beiSichtbarkeit = () => {
      if (document.visibilityState === 'visible') ladeUebersicht(false);
    };
    document.addEventListener('visibilitychange', beiSichtbarkeit);

    return () => {
      clearInterval(intervall);
      document.removeEventListener('visibilitychange', beiSichtbarkeit);
    };
  }, [ladeUebersicht]);

  const openCompany = (c: OverviewCompany) => navigate('/companies', { state: { openCompanyId: c.id } });
  const openCalendar = () => navigate('/calendar');

  // Volles „Kandidaten-Daten-Dashboard" laden und als Modal öffnen
  const openFullDashboard = async (c: OverviewCandidate) => {
    try {
      setLoadingFull(true);
      const res = await candidatesService.getCandidate(c.id);
      if (res.success && res.data) {
        setModalCandidate(res.data);
        setModalOpen(true);
        setPopupCandidate(null);
      }
    } catch (e) {
      console.error('getCandidate error:', e);
    } finally {
      setLoadingFull(false);
    }
  };

  const renderCandidate = (c: OverviewCandidate, zeitpunkt: string | null | undefined = c.createdAt) => (
    <Row key={c.id} onClick={() => setPopupCandidate(c)}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ ...primaryText, flex: '0 1 auto' }}>{c.name}</span>
          <StatusBadge stage={c.stage} />
        </div>
        <div style={secondaryText}>
          {c.position || 'Position unbekannt'}
          {c.editedBy ? ` · 👤 ${c.editedBy}` : ''}
        </div>
      </div>
      <div style={{ ...secondaryText, whiteSpace: 'nowrap' }}>{relativeDay(zeitpunkt)}</div>
    </Row>
  );

  const renderCompany = (c: OverviewCompany) => (
    <Row key={c.id} onClick={() => openCompany(c)}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={primaryText}>{c.name}</div>
      </div>
      <div style={{ ...secondaryText, whiteSpace: 'nowrap' }}>{relativeDay(c.createdAt)}</div>
    </Row>
  );

  const renderUserAppointments = (u: OverviewUserAppointments) => (
    <div
      key={u.userId}
      onClick={openCalendar}
      style={{
        cursor: 'pointer',
        padding: '12px',
        borderRadius: 'var(--radius-md)',
        background: 'var(--glass)',
        border: '1px solid var(--glass-border)',
        minWidth: '180px',
        flex: '1 1 200px',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: u.count ? '8px' : 0 }}>
        <span style={primaryText}>{u.userName}</span>
        <span
          style={{
            marginLeft: 'auto',
            fontSize: '0.75rem',
            fontWeight: 700,
            color: u.count ? 'var(--accent-primary)' : 'var(--text-tertiary)',
            background: u.count ? 'rgba(59,130,246,0.12)' : 'transparent',
            borderRadius: '999px',
            padding: '2px 8px',
          }}
        >
          {u.count}
        </span>
      </div>
      {u.appointments.slice(0, 4).map((a) => (
        <div key={a.id} style={{ ...secondaryText, display: 'flex', gap: '6px' }}>
          <span style={{ fontWeight: 600 }}>{timeShort(a.startTime)}</span>
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.title}</span>
        </div>
      ))}
    </div>
  );

  if (loading) {
    return <div style={{ padding: 'var(--spacing-xl)', color: 'var(--text-secondary)' }}>Übersicht wird geladen…</div>;
  }
  if (error || !data) {
    return <div style={{ padding: 'var(--spacing-xl)', color: 'var(--text-secondary)' }}>{error || 'Keine Daten.'}</div>;
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      style={{ padding: 'var(--spacing-lg)' }}
    >
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 'var(--spacing-lg)' }}>
        <h1 style={{ fontSize: '1.6rem', fontWeight: 800, color: 'var(--text-primary)', margin: 0 }}>Übersicht</h1>
        <div style={{ color: 'var(--text-secondary)', fontSize: '0.95rem', textTransform: 'capitalize' }}>{todayLong()}</div>
      </div>

      {/* Kennzahlen aus dem fruheren Recruiting-Dashboard — Klick filtert die
          Kandidatenliste nach der jeweiligen Stufe. */}
      <div style={{ marginBottom: 'var(--spacing-lg)' }}>
        <KpiRow kpis={kpis} loading={loading} />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 'var(--spacing-lg)' }}>
        <DashboardCard
          icon="🆕"
          title="Zuletzt hinzugefügt"
          subtitle="Die 20 zuletzt angelegten Kandidaten – neueste zuerst"
          count={data.latestCandidates.length}
          emptyText="Noch keine Kandidaten angelegt."
        >
          {data.latestCandidates.map((c) => renderCandidate(c))}
        </DashboardCard>

        <DashboardCard
          icon="📅"
          title="Im Interview"
          subtitle="Offene Interviews bzw. Vorstellungen im Interview-Status – zuletzt bewegte zuerst"
          count={data.inInterviewTotal ?? data.inInterview.length}
          emptyText="Derzeit niemand im Interview."
        >
          {data.inInterview.map((c) => renderCandidate(c, c.lastMovementAt || c.createdAt))}
        </DashboardCard>


        <DashboardCard
          icon="🏢"
          title="Neue Kunden – dieser Monat"
          subtitle="Diesen Monat angelegte Unternehmen"
          count={data.newCompaniesThisMonth.length}
          emptyText="Diesen Monat noch keine neuen Kunden."
        >
          {data.newCompaniesThisMonth.map(renderCompany)}
        </DashboardCard>

        <DashboardCard
          icon="👥"
          title="Termine je Mitarbeiter – heute"
          subtitle="Heutige Termine pro Mitarbeiter (Klick öffnet den Kalender)"
          count={data.appointmentsByUserToday.reduce((s, u) => s + u.count, 0)}
          emptyText="Heute keine Termine."
          wide
        >
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
            {data.appointmentsByUserToday.map(renderUserAppointments)}
          </div>
        </DashboardCard>
      </div>

      {/* Kandidaten-Popup: Kurzinfo + Absprung ins volle Kandidaten-Dashboard */}
      {popupCandidate && (
        <div
          onClick={() => setPopupCandidate(null)}
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 1000,
            background: 'rgba(0,0,0,0.45)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 'var(--spacing-md)',
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'var(--bg-primary, #ffffff)',
              borderRadius: 'var(--radius-lg)',
              border: '1px solid var(--glass-border)',
              padding: 'var(--spacing-lg)',
              width: 'min(440px, 92vw)',
              boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px' }}>
              <h3 style={{ fontSize: '1.2rem', fontWeight: 700, color: 'var(--text-primary)', margin: 0 }}>
                {popupCandidate.name}
              </h3>
              <StatusBadge stage={popupCandidate.stage} />
            </div>
            <div style={{ ...secondaryText, marginBottom: 'var(--spacing-md)' }}>
              {popupCandidate.position || 'Position unbekannt'}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: 'var(--spacing-lg)' }}>
              <InfoLine label="Status" value={stageLabel(popupCandidate.stage)} />
              <InfoLine label="Bearbeitet von" value={popupCandidate.editedBy || 'Nicht bekannt'} />
              <InfoLine label="Angelegt" value={relativeDay(popupCandidate.createdAt)} />
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                className="btn btn-primary"
                disabled={loadingFull}
                onClick={() => openFullDashboard(popupCandidate)}
                style={{ flex: 1 }}
              >
                {loadingFull ? 'Öffne…' : 'Kandidaten-Dashboard öffnen'}
              </button>
              <button className="btn btn-ghost" onClick={() => setPopupCandidate(null)}>
                Schließen
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Volle Detailansicht („Kandidaten-Daten-Dashboard") */}
      {modalOpen && modalCandidate && (
        <CandidateDetailModalV2
          candidate={modalCandidate}
          isOpen={modalOpen}
          onClose={() => {
            setModalOpen(false);
            setModalCandidate(null);
          }}
          onUpdate={(u: Candidate) => setModalCandidate(u)}
        />
      )}
    </motion.div>
  );
};
